import React, { useState, useEffect } from "react";
import { useUser } from "../../components/UserContext";
import axiosInstance from "../../api/axiosInstance";
import GlobalLoader from "../../components/ui/GlobalLoader";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../../components/ui/card";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../../components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
  ChartLegend,
  ChartLegendContent,
} from "../../components/ui/chart";
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  AreaChart,
  Area,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  ComposedChart,
} from "recharts";
import {
  Users,
  Package,
  DollarSign,
  Clock,
  Activity,
  Truck,
  CheckCircle,
  Star,
  Target,
  Zap,
  Heart,
  Pill,
  ShoppingCart,
} from "lucide-react";

import { useAppSelector } from "@/redux/hooks";

export function PharmacyAnalyticsPage() {
  // const { user } = useUser();
  const user = useAppSelector((state) => state.auth.user);
  const [timeRange, setTimeRange] = useState("30d");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Utility function to check if data exists (including zero values)
  const hasData = (data, key = null) => {
    if (!data || !Array.isArray(data) || data.length === 0) return false;
    if (!key) return true;
    // Allow zero values, just check if the key exists
    return data.some(item => item.hasOwnProperty(key) && item[key] !== null && item[key] !== undefined);
  };
  
  // State for analytics data
  const [deliveryInsightsData, setDeliveryInsightsData] = useState([]);
  const [patientDemographics, setPatientDemographics] = useState([]);
  const [revenueData, setRevenueData] = useState([]);
  const [orderStatusData, setOrderStatusData] = useState([]);
  const [medicationCategories, setMedicationCategories] = useState([]);
  const [satisfactionData, setSatisfactionData] = useState([]);
  const [hourlyData, setHourlyData] = useState([]);
  const [deliveryTypeBreakdown, setDeliveryTypeBreakdown] = useState(null);
  const [customerRetention, setCustomerRetention] = useState(null);
  const [equipmentUsage, setEquipmentUsage] = useState(null);
  const [growthMetrics, setGrowthMetrics] = useState(null);
  const [operationalKPIs, setOperationalKPIs] = useState(null);
  
  const defaultChartConfig = {
    deliveries: { label: "Deliveries", color: "#3b82f6" },
    successRate: { label: "Success Rate", color: "#10b981" },
    avgTime: { label: "Avg Time (min)", color: "#f59e0b" },
    revenue: { label: "Revenue", color: "#3b82f6" },
    expenses: { label: "Expenses", color: "#ef4444" },
    profit: { label: "Profit", color: "#10b981" },
  };
  
  const [chartConfig, setChartConfig] = useState(defaultChartConfig);

  // Fetch analytics data from API
  const fetchAnalyticsData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await axiosInstance.get(`/analytics/all?timeRange=${timeRange}`);
      console.log('Full API Response:', response.data);
      
      // Extract the nested data object from response
      const analyticsData = response.data.data;
      console.log('Extracted analytics data:', analyticsData);
      
      // Update all state with API data
      setDeliveryInsightsData(analyticsData.deliveryInsights || []);
      setPatientDemographics(analyticsData.patientDemographics || []);
      setRevenueData(analyticsData.revenueData || []);
      setOrderStatusData(analyticsData.orderStatus || []);
      setMedicationCategories(analyticsData.medicationCategories || []);
      setSatisfactionData(analyticsData.satisfactionData || []);
      setHourlyData(analyticsData.hourlyData || []);
      setDeliveryTypeBreakdown(analyticsData.deliveryTypeBreakdown || null);
      setCustomerRetention(analyticsData.customerRetention || null);
      setEquipmentUsage(analyticsData.equipmentUsage || null);
      setGrowthMetrics(analyticsData.growthMetrics || null);
      setOperationalKPIs(analyticsData.operationalKPIs || null);
      setChartConfig(analyticsData.chartConfig || {
        deliveries: { label: "Deliveries", color: "#3b82f6" },
        successRate: { label: "Success Rate", color: "#10b981" },
        avgTime: { label: "Avg Time (min)", color: "#f59e0b" },
        revenue: { label: "Revenue", color: "#3b82f6" },
        expenses: { label: "Expenses", color: "#ef4444" },
        profit: { label: "Profit", color: "#10b981" }
      });
      
      console.log('State updated with data lengths:', {
        deliveryInsights: analyticsData.deliveryInsights?.length || 0,
        patientDemographics: analyticsData.patientDemographics?.length || 0,
        revenueData: analyticsData.revenueData?.length || 0,
        orderStatus: analyticsData.orderStatus?.length || 0,
        medicationCategories: analyticsData.medicationCategories?.length || 0,
        satisfactionData: analyticsData.satisfactionData?.length || 0
      });
      
      console.log('Data validation results:', {
        hasDeliveryData: hasData(analyticsData.deliveryInsights, 'deliveries'),
        hasSuccessRateData: hasData(analyticsData.deliveryInsights, 'successRate'),
        hasAvgTimeData: hasData(analyticsData.deliveryInsights, 'avgTime'),
        hasPatientData: hasData(analyticsData.patientDemographics, 'count'),
        hasRevenueData: hasData(analyticsData.revenueData, 'revenue'),
        hasMedicationData: hasData(analyticsData.medicationCategories, 'prescriptions')
      });
      
      console.log('Sample delivery insights data:', analyticsData.deliveryInsights?.slice(0, 2));
      console.log('Patient Demographics data:', analyticsData.patientDemographics);
      console.log('Satisfaction data:', analyticsData.satisfactionData);
      
    } catch (err) {
      setError(err.message);
      console.error('Error fetching analytics data:', err);
    } finally {
      setLoading(false);
    }
  };

  // Fetch data on component mount and when timeRange changes
  useEffect(() => {
    fetchAnalyticsData();
  }, [timeRange]);


  const exportReport = async () => {
    try {
      const res = await axiosInstance.get(`/analytics/export?timeRange=${timeRange}`, {
        responseType: "blob", // to download files
      });
  
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `analytics-report-${timeRange}.pdf`);
      document.body.appendChild(link);
      link.click();
    } catch (err) {
      console.error("Export failed:", err);
    }
  };

  
  // Loading state
  if (loading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Loading analytics data...</p>
          </div>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="text-red-500 mb-4">
              <Package className="w-12 h-12 mx-auto" />
            </div>
            <p className="text-red-600 font-medium mb-2">Error loading analytics data</p>
            <p className="text-gray-600 mb-4">{error}</p>
            <Button onClick={fetchAnalyticsData} className="bg-blue-600 hover:bg-blue-700">
              Try Again
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div>
          {/* <h1 className="text-3xl font-semibold text-gray-900">
            Analytics Dashboard
          </h1> */}
          <p className="text-gray-600 mt-1">
            Comprehensive insights for {user?.pharmacyName}
          </p>
        </div>
        <div className="flex gap-3">
          <Select value={timeRange} onValueChange={setTimeRange}>
            <SelectTrigger className="w-32">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="7d">Last 7 days</SelectItem>
              <SelectItem value="30d">Last 30 days</SelectItem>
              <SelectItem value="90d">Last 90 days</SelectItem>
              <SelectItem value="1y">Last year</SelectItem>
            </SelectContent>
          </Select>
          {/* <Button 
            onClick={fetchAnalyticsData} 
            disabled={loading}
            variant="outline"
          >
            <Activity className="w-4 h-4 mr-2" />
            {loading ? "Refreshing..." : "Refresh"}
          </Button> */}
          <Button variant="outline" onClick={exportReport}>
            <Activity className="w-4 h-4 mr-2" />
            Export Report
          </Button>
        </div>
      </div>

      {/* Key Metrics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <Card className="border-l-4 border-l-blue-500">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                 <p className="text-sm font-medium text-gray-600">
                   Total Orders
                 </p>
                <p className="text-2xl font-bold text-gray-900">
                  {deliveryInsightsData.reduce((sum, day) => sum + (day.deliveries || 0), 0)}
                </p>
                {growthMetrics ? (
                  <p className="text-xs text-blue-600 mt-1">
                    +{growthMetrics.deliveryGrowth}% {growthMetrics.period}
                  </p>
                ) : (
                  <p className="text-xs text-gray-400 mt-1">Growth data not available</p>
                )}
              </div>
              <Truck className="w-8 h-8 text-blue-600" />
            </div>
          </CardContent>
        </Card>

        <Card className="border-l-4 border-l-green-500">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                 <p className="text-sm font-medium text-gray-600">
                   Success Rate
                 </p>
                <p className="text-2xl font-bold text-gray-900">
                  {deliveryInsightsData.length > 0 
                    ? `${(deliveryInsightsData.reduce((sum, day) => sum + (day.successRate || 0), 0) / deliveryInsightsData.length).toFixed(1)}%`
                    : '0%'
                  }
                </p>
                {growthMetrics ? (
                  <p className="text-xs text-green-600 mt-1">
                    +{growthMetrics.efficiencyImprovement}% efficiency improvement
                  </p>
                ) : (
                  <p className="text-xs text-gray-400 mt-1">Improvement data not available</p>
                )}
              </div>
              <CheckCircle className="w-8 h-8 text-green-600" />
            </div>
          </CardContent>
        </Card>

        <Card className="border-l-4 border-l-purple-500">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                 <p className="text-sm font-medium text-gray-600">
                   Avg Delivery Time
                 </p>
                <p className="text-2xl font-bold text-gray-900">
                  {deliveryInsightsData.length > 0 
                    ? `${Math.round(deliveryInsightsData.reduce((sum, day) => sum + (day.avgTime || 0), 0) / deliveryInsightsData.length)} min`
                    : '0 min'
                  }
                </p>
                {growthMetrics ? (
                  <p className="text-xs text-purple-600 mt-1">
                    {growthMetrics.efficiencyImprovement} min faster
                  </p>
                ) : (
                  <p className="text-xs text-gray-400 mt-1">Time improvement data not available</p>
                )}
              </div>
              <Clock className="w-8 h-8 text-purple-600" />
            </div>
          </CardContent>
        </Card>

        <Card className="border-l-4 border-l-orange-500">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
      <div>
                 <p className="text-sm font-medium text-gray-600">
                   Customer Rating
                 </p>
                 <p className="text-2xl font-bold text-gray-900">
                   {satisfactionData.length > 0 
                     ? (satisfactionData.reduce((sum, item) => sum + (item.rating || 0), 0) / satisfactionData.length).toFixed(1)
                     : '0.0'
                   }
                 </p>
                 <p className="text-xs text-orange-600 mt-1">Out of 5.0 stars</p>
              </div>
              <Star className="w-8 h-8 text-orange-600" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Analytics Tabs */}
      <Tabs defaultValue="delivery" className="space-y-6">
        <TabsList className="grid w-full grid-cols-5 bg-gray-100 p-1 rounded-lg">
          <TabsTrigger
            value="delivery"
            className="data-[state=active]:bg-white"
          >
            <Truck className="w-4 h-4 mr-2" />
            Delivery Insights
          </TabsTrigger>
          <TabsTrigger
            value="customers"
            className="data-[state=active]:bg-white"
          >
            <Users className="w-4 h-4 mr-2" />
            Patients/Customers
          </TabsTrigger>
          <TabsTrigger
            value="operations"
            className="data-[state=active]:bg-white"
          >
            <Activity className="w-4 h-4 mr-2" />
            Operations
          </TabsTrigger>
          <TabsTrigger value="revenue" className="data-[state=active]:bg-white">
            <DollarSign className="w-4 h-4 mr-2" />
            Revenue & Expenses
          </TabsTrigger>
          <TabsTrigger value="orders" className="data-[state=active]:bg-white">
            <Package className="w-4 h-4 mr-2" />
            Orders & Deliveries
          </TabsTrigger>
        </TabsList>

        {/* Delivery Insights Tab */}
        <TabsContent value="delivery" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle>Weekly Delivery Performance</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-[300px]">
                  {hasData(deliveryInsightsData, 'deliveries') ? (
                    <ChartContainer config={chartConfig} className="h-full">
                      <BarChart data={deliveryInsightsData} width={400} height={300}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e0e4e7" />
                        <XAxis 
                          dataKey="day" 
                          tick={{ fontSize: 12, fill: '#6b7280' }}
                          axisLine={{ stroke: '#d1d5db' }}
                        />
                        <YAxis 
                          tick={{ fontSize: 12, fill: '#6b7280' }}
                          axisLine={{ stroke: '#d1d5db' }}
                        />
                        <ChartTooltip 
                          content={<ChartTooltipContent hideLabel={false}  />}
                          // cursor={{ fill: 'rgba(59, 130, 246, 0.1)' }}
                          cursor={false}
                        />
                        <Bar
                          dataKey="deliveries"
                          fill="#3b82f6"
                          stroke="#2563eb"
                          strokeWidth={1}
                          radius={[4, 4, 0, 0]}
                        />
                      </BarChart>
                    </ChartContainer>
                  ) : (
                    <div className="flex items-center justify-center h-full">
                      <p className="text-gray-500">No delivery data available</p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Success Rate Trends</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-[300px]">
                  {hasData(deliveryInsightsData, 'successRate') ? (
                    <ChartContainer config={chartConfig} className="h-full">
                      <LineChart data={deliveryInsightsData} width={400} height={300}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e0e4e7" />
                        <XAxis 
                          dataKey="day" 
                          tick={{ fontSize: 12, fill: '#6b7280' }}
                          axisLine={{ stroke: '#d1d5db' }}
                        />
                        <YAxis 
                          tick={{ fontSize: 12, fill: '#6b7280' }}
                          axisLine={{ stroke: '#d1d5db' }}
                        />
                        <ChartTooltip 
                          content={<ChartTooltipContent hideLabel={false} />}
                          cursor={{ stroke: 'rgba(16, 185, 129, 0.3)' }}
                        />
                        <Line
                          type="monotone"
                          dataKey="successRate"
                          stroke="#10b981"
                          strokeWidth={4}
                          strokeOpacity={1}
                          dot={{ fill: '#10b981', strokeWidth: 2, r: 5, fillOpacity: 1 }}
                          activeDot={{ r: 8, fill: '#059669', strokeWidth: 2, stroke: '#fff' }}
                          connectNulls={false}
                        />
                      </LineChart>
                    </ChartContainer>
                  ) : (
                    <div className="flex items-center justify-center h-full">
                      <p className="text-gray-500">No success rate data available</p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle>Average Delivery Times</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-[300px]">
                  {hasData(deliveryInsightsData, 'avgTime') ? (
                    <ChartContainer config={chartConfig} className="h-full">
                      <AreaChart data={deliveryInsightsData} width={400} height={300}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e0e4e7" />
                        <XAxis 
                          dataKey="day" 
                          tick={{ fontSize: 12, fill: '#6b7280' }}
                          axisLine={{ stroke: '#d1d5db' }}
                        />
                        <YAxis 
                          tick={{ fontSize: 12, fill: '#6b7280' }}
                          axisLine={{ stroke: '#d1d5db' }}
                        />
                        <ChartTooltip 
                          content={<ChartTooltipContent hideLabel={false} />}
                          cursor={{ stroke: 'rgba(245, 158, 11, 0.3)' }}
                        />
                        <Area
                          type="monotone"
                          dataKey="avgTime"
                          stroke="#f59e0b"
                          strokeWidth={4}
                          strokeOpacity={1}
                          fill="#f59e0b"
                          fillOpacity={0.3}
                          dot={{ fill: '#f59e0b', strokeWidth: 2, r: 5, fillOpacity: 1 }}
                          activeDot={{ r: 8, fill: '#d97706', strokeWidth: 2, stroke: '#fff' }}
                          connectNulls={false}
                        />
                      </AreaChart>
                    </ChartContainer>
                  ) : (
                    <div className="flex items-center justify-center h-full">
                      <p className="text-gray-500">No delivery time data available</p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Delivery Status Overview</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-[300px]">
                  {orderStatusData && orderStatusData.length > 0 ? (
                    <ChartContainer config={chartConfig} className="h-full">
                      <PieChart width={400} height={300}>
                        <Pie
                          data={orderStatusData}
                          cx="50%"
                          cy="50%"
                          outerRadius={100}
                          innerRadius={40}
                          dataKey="value"
                          label={({ name, value, percent }) => 
                            `${name}: ${value} (${(percent * 100).toFixed(0)}%)`
                          }
                          labelLine={false}
                        >
                          {orderStatusData.map((entry, index) => (
                            <Cell 
                              key={`cell-${index}`} 
                              fill={entry.color} 
                              stroke="#fff"
                              strokeWidth={2}
                            />
                          ))}
                        </Pie>
                        <ChartTooltip 
                          content={<ChartTooltipContent hideLabel={true} />}
                        />
                      </PieChart>
                    </ChartContainer>
                  ) : (
                    <div className="flex items-center justify-center h-full">
                      <p className="text-gray-500">No order status data available</p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Patients/Customers Tab */}
        <TabsContent value="customers" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle>Customer Demographics</CardTitle>
                <div className="text-xs text-gray-500 mt-1">
                  Data: {patientDemographics?.map(d => `${d.age}: ${d.count}`).join(', ')}
                </div>
              </CardHeader>
              <CardContent>
                <div className="h-[300px]">
                  {patientDemographics && patientDemographics.length > 0 ? (
                    <ChartContainer config={chartConfig} className="h-full">
                      <BarChart 
                        data={patientDemographics} 
                        layout="horizontal"
                        width={400} 
                        height={300}
                      >
                        <CartesianGrid strokeDasharray="3 3" stroke="#e0e4e7" />
                        <XAxis 
                          dataKey="age" 
                          type="category" 
                          tick={{ fontSize: 12, fill: '#6b7280' }}
                          axisLine={{ stroke: '#d1d5db' }}
                        />
                        <YAxis 
                          type="number" 
                          domain={[0, 'dataMax + 1']}
                          tick={{ fontSize: 12, fill: '#6b7280' }}
                          axisLine={{ stroke: '#d1d5db' }}
                        />
                        <ChartTooltip 
                          content={<ChartTooltipContent hideLabel={false} />}
                          // cursor={{ fill: 'rgba(59, 130, 246, 0.1)' }}
                          cursor={false}
                        />
                        <Bar
                          dataKey="count"
                          fill="#3b82f6"
                          stroke="#2563eb"
                          strokeWidth={2}
                          radius={[4, 4, 0, 0]}
                          minPointSize={5}
                        />
                      </BarChart>
                    </ChartContainer>
                  ) : (
                    <div className="flex items-center justify-center h-full">
                      <p className="text-gray-500">No patient demographic data available</p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Customer Satisfaction Ratings</CardTitle>
                <div className="text-xs text-gray-500 mt-1">
                  Data: {satisfactionData?.map(d => `${d.category}: ${d.rating}`).join(', ')}
                </div>
              </CardHeader>
              <CardContent>
                <div className="h-[300px]">
                  {satisfactionData && satisfactionData.length > 0 ? (
                    <ChartContainer config={chartConfig} className="h-full">
                      <BarChart 
                        data={satisfactionData} 
                        layout="horizontal"
                        width={400} 
                        height={300}
                      >
                        <CartesianGrid strokeDasharray="3 3" stroke="#e0e4e7" />
                       

                        <XAxis 
                          dataKey="category" 
                          type="category" 
                          tick={{ fontSize: 12, fill: '#6b7280' }}
                          axisLine={{ stroke: '#d1d5db' }}
                        />
                        <YAxis 
                          type="number" 
                          domain={[0, 5]} 
                          tick={{ fontSize: 12, fill: '#6b7280' }}
                          axisLine={{ stroke: '#d1d5db' }}
                          width={120}
                        />
                        <ChartTooltip 
                          content={<ChartTooltipContent hideLabel={false} />}
                          // cursor={{ fill: 'rgba(16, 185, 129, 0.1)' }}
                          cursor={false}
                        />
                        <Bar
                          dataKey="rating"
                          fill="#10b981"
                          stroke="#059669"
                          strokeWidth={1}
                          radius={[0, 4, 4, 0]}
                        />
                      </BarChart>
                    </ChartContainer>
                  ) : (
                    <div className="flex items-center justify-center h-full">
                      <p className="text-gray-500">No satisfaction data available</p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <Card>
              <CardHeader>
                <CardTitle>Customer Retention</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-center">
                  {customerRetention ? (
                    <>
                      <div className="text-3xl font-bold text-green-600 mb-2">
                        {customerRetention.retentionRate}%
                      </div>
                      <p className="text-sm text-gray-600">
                        Customer Retention Rate
                      </p>
                      <div className="mt-4 space-y-2">
                        <div className="flex justify-between text-sm">
                          <span>New Customers</span>
                          <span className="font-medium">{customerRetention.newCustomers}</span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span>Returning Customers</span>
                          <span className="font-medium">{customerRetention.returningCustomers}</span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span>Total Customers</span>
                          <span className="font-medium">{customerRetention.totalCustomers}</span>
                        </div>
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="text-3xl font-bold text-gray-400 mb-2">
                        N/A
                      </div>
                      <p className="text-sm text-gray-600">
                        Customer Retention Rate
                      </p>
                      <p className="text-xs text-gray-500 mt-2">
                        Data not available
                      </p>
                    </>
                  )}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Top Customer Segments</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <Heart className="w-4 h-4 text-red-500 mr-2" />
                      <span className="text-sm">Chronic Care</span>
                    </div>
                    <Badge variant="outline">45%</Badge>
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <Pill className="w-4 h-4 text-blue-500 mr-2" />
                      <span className="text-sm">Acute Care</span>
                    </div>
                    <Badge variant="outline">32%</Badge>
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <ShoppingCart className="w-4 h-4 text-green-500 mr-2" />
                      <span className="text-sm">OTC Products</span>
                    </div>
                    <Badge variant="outline">23%</Badge>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Monthly Growth</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div>
                    <div className="flex justify-between text-sm mb-1">
                      <span>New Patient Registrations</span>
                      <span className="font-medium text-green-600">+18%</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div
                        className="bg-green-500 h-2 rounded-full"
                        style={{ width: "0%" }} /* TODO: Calculate from analytics data */
                      ></div>
                    </div>
                  </div>
                  <div>
                    <div className="flex justify-between text-sm mb-1">
                      <span>Prescription Refills</span>
                      {operationalKPIs ? (
                        <span className="font-medium text-blue-600">{operationalKPIs.prescriptionRefillRate}%</span>
                      ) : (
                        <span className="font-medium text-gray-400">N/A</span>
                      )}
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div
                        className="bg-blue-500 h-2 rounded-full"
                        style={{ width: operationalKPIs ? `${operationalKPIs.prescriptionRefillRate}%` : "0%" }}
                      ></div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Operations Tab */}
        <TabsContent value="operations" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <Card>
              <CardHeader>
                <CardTitle>Operational KPIs</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <Target className="w-4 h-4 text-blue-500 mr-2" />
                      <span className="text-sm">Order Processing Time</span>
                    </div>
                    <span className="font-bold text-blue-600">12 min</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <Zap className="w-4 h-4 text-green-500 mr-2" />
                      <span className="text-sm">Efficiency Rate</span>
                    </div>
                    <span className="font-bold text-green-600">94%</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <Clock className="w-4 h-4 text-orange-500 mr-2" />
                      <span className="text-sm">Peak Hours</span>
                    </div>
                    <span className="font-bold text-orange-600">2-4 PM</span>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Staff Performance</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm">Pharmacist Efficiency</span>
                    <Badge className="bg-green-100 text-green-800">98%</Badge>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm">Technician Speed</span>
                    <Badge className="bg-blue-100 text-blue-800">92%</Badge>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm">Quality Control</span>
                    <Badge className="bg-purple-100 text-purple-800">99%</Badge>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Resource Utilization</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <div>
                    <div className="flex justify-between text-sm mb-1">
                      <span>Equipment Usage</span>
                      {/* <span className="font-medium">87%</span> TODO: Calculate from analytics */}
                      <span className="font-medium text-gray-400">N/A</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div
                        className="bg-blue-500 h-2 rounded-full"
                        style={{ width: "0%" }} /* TODO: Calculate from analytics data */
                      ></div>
                    </div>
                  </div>
                  <div>
                    <div className="flex justify-between text-sm mb-1">
                      <span>Equipment Usage</span>
                      {equipmentUsage ? (
                        <span className="font-medium">{equipmentUsage.usageRate}%</span>
                      ) : (
                        <span className="font-medium text-gray-400">N/A</span>
                      )}
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div
                        className="bg-green-500 h-2 rounded-full"
                        style={{ width: equipmentUsage ? `${equipmentUsage.usageRate}%` : "0%" }}
                      ></div>
                    </div>
                    {equipmentUsage && (
                      <div className="mt-2 text-xs text-gray-500">
                        {equipmentUsage.activeEquipment}/{equipmentUsage.totalEquipment} {equipmentUsage.equipmentType}
                      </div>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Revenue & Expenses Tab */}
        <TabsContent value="revenue" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle>Revenue vs Expenses</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-[300px]">
                  {revenueData && revenueData.length > 0 ? (
                    <ChartContainer config={chartConfig} className="h-full">
                      <ComposedChart data={revenueData} width={400} height={300}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e0e4e7" />
                        <XAxis 
                          dataKey="month" 
                          tick={{ fontSize: 12, fill: '#6b7280' }}
                          axisLine={{ stroke: '#d1d5db' }}
                        />
                        <YAxis 
                          tick={{ fontSize: 12, fill: '#6b7280' }}
                          axisLine={{ stroke: '#d1d5db' }}
                        />
                        <ChartTooltip 
                          content={<ChartTooltipContent hideLabel={false} />}
                          cursor={{ fill: 'rgba(59, 130, 246, 0.1)' }}
                        />
                        <Bar
                          dataKey="revenue"
                          fill="#3b82f6"
                          stroke="#2563eb"
                          strokeWidth={1}
                          radius={[2, 2, 0, 0]}
                        />
                        <Bar
                          dataKey="expenses"
                          fill="#ef4444"
                          stroke="#dc2626"
                          strokeWidth={1}
                          radius={[2, 2, 0, 0]}
                        />
                        <Line
                          type="monotone"
                          dataKey="profit"
                          stroke="#10b981"
                          strokeWidth={4}
                          strokeOpacity={1}
                          dot={{ fill: '#10b981', strokeWidth: 2, r: 5, fillOpacity: 1 }}
                          activeDot={{ r: 8, fill: '#059669', strokeWidth: 2, stroke: '#fff' }}
                          connectNulls={false}
                        />
                      </ComposedChart>
                    </ChartContainer>
                  ) : (
                    <div className="flex items-center justify-center h-full">
                      <p className="text-gray-500">No revenue data available</p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Profit Trends</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-[300px]">
                  {hasData(revenueData, 'profit') ? (
                    <ChartContainer config={chartConfig} className="h-full">
                      <AreaChart data={revenueData} width={400} height={300}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e0e4e7" />
                        <XAxis 
                          dataKey="month" 
                          tick={{ fontSize: 12, fill: '#6b7280' }}
                          axisLine={{ stroke: '#d1d5db' }}
                        />
                        <YAxis 
                          tick={{ fontSize: 12, fill: '#6b7280' }}
                          axisLine={{ stroke: '#d1d5db' }}
                        />
                        <ChartTooltip 
                          content={<ChartTooltipContent hideLabel={false} />}
                          cursor={{ stroke: 'rgba(16, 185, 129, 0.3)' }}
                        />
                        <Area
                          type="monotone"
                          dataKey="profit"
                          stroke="#10b981"
                          strokeWidth={4}
                          strokeOpacity={1}
                          fill="#10b981"
                          fillOpacity={0.3}
                          dot={{ fill: '#10b981', strokeWidth: 2, r: 5, fillOpacity: 1 }}
                          activeDot={{ r: 8, fill: '#059669', strokeWidth: 2, stroke: '#fff' }}
                          connectNulls={false}
                        />
                      </AreaChart>
                    </ChartContainer>
                  ) : (
                    <div className="flex items-center justify-center h-full">
                      <p className="text-gray-500">No profit trend data available</p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <Card>
              <CardHeader>
                <CardTitle>Revenue Breakdown</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <Pill className="w-4 h-4 text-blue-500 mr-2" />
                      <span className="text-sm">Prescription Sales</span>
                    </div>
                    <span className="font-bold text-blue-600">$12,450</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <ShoppingCart className="w-4 h-4 text-green-500 mr-2" />
                      <span className="text-sm">OTC Products</span>
                    </div>
                    <span className="font-bold text-green-600">$3,200</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <Truck className="w-4 h-4 text-purple-500 mr-2" />
                      <span className="text-sm">Delivery Fees</span>
                    </div>
                    <span className="font-bold text-purple-600">$1,850</span>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Expense Categories</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <span className="text-sm">Staff Salaries</span>
                    <span className="font-bold text-red-600">$4,200</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm">Inventory</span>
                    <span className="font-bold text-orange-600">$3,800</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm">Operations</span>
                    <span className="font-bold text-yellow-600">$1,200</span>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Financial Health</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-center">
                  <div className="text-3xl font-bold text-green-600 mb-2">
                    ${revenueData.length > 0 
                      ? revenueData[revenueData.length - 1]?.profit?.toLocaleString() || '0'
                      : '0'
                    }
                  </div>
                  <p className="text-sm text-gray-600 mb-4">Monthly Profit</p>
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span>Profit Margin</span>
                      {growthMetrics ? (
                        <span className="font-medium text-green-600">+{growthMetrics.revenueGrowth}%</span>
                      ) : (
                        <span className="font-medium text-gray-400">N/A</span>
                      )}
                    </div>
                    <div className="flex justify-between text-sm">
                      <span>Growth Rate</span>
                      <span className="font-medium text-blue-600">+18%</span>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Orders & Deliveries Tab */}
        <TabsContent value="orders" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle>Order Volume Trends</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-[300px]">
                  {hasData(deliveryInsightsData, 'deliveries') ? (
                    <ChartContainer config={chartConfig} className="h-full">
                      <LineChart data={deliveryInsightsData} width={400} height={300}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e0e4e7" />
                        <XAxis 
                          dataKey="day" 
                          tick={{ fontSize: 12, fill: '#6b7280' }}
                          axisLine={{ stroke: '#d1d5db' }}
                        />
                        <YAxis 
                          tick={{ fontSize: 12, fill: '#6b7280' }}
                          axisLine={{ stroke: '#d1d5db' }}
                        />
                        <ChartTooltip 
                          content={<ChartTooltipContent hideLabel={false} />}
                          cursor={{ stroke: 'rgba(59, 130, 246, 0.3)' }}
                        />
                        <Line
                          type="monotone"
                          dataKey="deliveries"
                          stroke="#3b82f6"
                          strokeWidth={4}
                          strokeOpacity={1}
                          dot={{ fill: '#3b82f6', strokeWidth: 2, r: 5, fillOpacity: 1 }}
                          activeDot={{ r: 8, fill: '#2563eb', strokeWidth: 2, stroke: '#fff' }}
                          connectNulls={false}
                        />
                      </LineChart>
                    </ChartContainer>
                  ) : (
                    <div className="flex items-center justify-center h-full">
                      <p className="text-gray-500">No order volume data available</p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Medication Categories</CardTitle>
              </CardHeader>
              <CardContent>
                <ChartContainer config={chartConfig}>
                  <BarChart
                    data={medicationCategories}
                    layout="horizontal" // ✅ use vertical for horizontal bars
                  >
                    <CartesianGrid strokeDasharray="2 2" />
                    <XAxis dataKey="category" type="category" />
                    <YAxis type="number" />
                    <ChartTooltip
                      cursor={false}
                      content={<ChartTooltipContent />}
                    />
                    <Bar
                      dataKey="prescriptions"
                      fill="#f59e0b"
                      stroke="#d97706"
                      strokeWidth={1}
                    />
                  </BarChart>
                </ChartContainer>
              </CardContent>
            </Card>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <Card>
              <CardHeader>
                <CardTitle>Order Status Overview</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {orderStatusData.map((status, index) => (
                    <div
                      key={index}
                      className="flex items-center justify-between"
                    >
                      <div className="flex items-center">
                        <div
                          className="w-3 h-3 rounded-full mr-2"
                          style={{ backgroundColor: status.color }}
                        ></div>
                        <span className="text-sm">{status.name}</span>
                      </div>
                      <span className="font-bold">{status.value}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Delivery Performance</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm">On-Time Delivery</span>
                    {deliveryTypeBreakdown ? (
                      <Badge className="bg-green-100 text-green-800">
                        {deliveryTypeBreakdown.sameDay?.successRate?.toFixed(1) || 'N/A'}%
                      </Badge>
                    ) : (
                      <Badge className="bg-gray-100 text-gray-500">N/A</Badge>
                    )}
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm">Same-Day Delivery</span>
                    {deliveryTypeBreakdown ? (
                      <Badge className="bg-blue-100 text-blue-800">
                        {deliveryTypeBreakdown.sameDay?.successRate?.toFixed(1) || 'N/A'}%
                      </Badge>
                    ) : (
                      <Badge className="bg-gray-100 text-gray-500">N/A</Badge>
                    )}
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm">Next-Day Delivery</span>
                    {deliveryTypeBreakdown ? (
                      <Badge className="bg-purple-100 text-purple-800">
                        {deliveryTypeBreakdown.nextDay?.successRate?.toFixed(1) || 'N/A'}%
                      </Badge>
                    ) : (
                      <Badge className="bg-gray-100 text-gray-500">N/A</Badge>
                    )}
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm">Failed Deliveries</span>
                    <Badge className="bg-red-100 text-red-800">1.9%</Badge>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Top Performing Medications</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {medicationCategories.slice(0, 3).map((med, index) => (
                    <div
                      key={index}
                      className="flex items-center justify-between"
                    >
                      <div>
                        <p className="text-sm font-medium">{med.category}</p>
                        <p className="text-xs text-gray-500">
                          {med.prescriptions} prescriptions
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="text-sm font-bold text-green-600">
                          ${med.revenue.toLocaleString()}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>

      {/* Global loading overlay */}
      <GlobalLoader 
        isLoading={loading}
        message="Loading analytics data..."
        subMessage="Please wait while we fetch your pharmacy analytics"
      />
    </div>
  );
}